import {
  addNewEmployeeURL,
  autopaySetupGuidesURL,
  cancelCompanyPolicyURL,
  companyInformationURL,
  getHelpURL,
  healthPlanDetailsURL,
  manageEmployeesURL,
  managePayrollURL,
  payBillURL,
  policyManagementURL,
  renewalOverviewURL,
  requestInvoiceURL,
} from "Dashboard/EmployerUrls";

import type { CatalogEntry, ConciergeActions, NavTarget } from "../actions";

// Maps a backend feature id to a button label and click behavior. No entry → no button.
//
// The Employer Dashboard is a react-router SPA under /employer-dashboard. Most
// destinations are in-dashboard routes; a few (renewal, support) live elsewhere
// and full-load. `spaRoutes` (in the bundle below) tells ConciergeChat which
// paths to client-side push (history.push) vs. full-load.

export type DestinationKey =
  | "pay_bills"
  | "request_invoices"
  | "autopay_setup"
  | "add_employee"
  | "manage_employees"
  | "manage_payroll"
  | "manage_policy"
  | "health_plan_details"
  | "cancel_policy"
  | "company_information"
  | "renewal"
  | "support";

// Same relative path for both faces: full-load-safe today, SPA-pushable once wired.
const target = (url: string): NavTarget => ({ spaPath: url, absoluteUrl: url });

export const destinations: Record<DestinationKey, () => NavTarget> = {
  pay_bills: () => target(payBillURL),
  request_invoices: () => target(requestInvoiceURL),
  autopay_setup: () => target(autopaySetupGuidesURL),
  add_employee: () => target(addNewEmployeeURL),
  manage_employees: () => target(manageEmployeesURL),
  manage_payroll: () => target(managePayrollURL),
  manage_policy: () => target(policyManagementURL),
  health_plan_details: () => target(healthPlanDetailsURL),
  cancel_policy: () => target(cancelCompanyPolicyURL),
  company_information: () => target(companyInformationURL),
  renewal: () => target(renewalOverviewURL),
  support: () => target(getHelpURL),
};

// Keyed by backend feature id (must match employer_dashboard/catalog_v0.1.json).
export const catalog: Record<string, CatalogEntry<DestinationKey>> = {
  pay_bills: { label: "Go to Pay My Bills", target: "pay_bills" },
  request_invoices: { label: "Go to Request Invoices", target: "request_invoices" },
  autopay_setup: { label: "Set up autopay", target: "autopay_setup" },
  add_employee: { label: "Add a new employee", target: "add_employee" },
  manage_employees: { label: "Go to Manage Employees", target: "manage_employees" },
  manage_payroll: { label: "Go to Manage Payroll", target: "manage_payroll" },
  manage_policy: { label: "Go to Manage Policy", target: "manage_policy" },
  health_plan_details: { label: "View Health Plan Details", target: "health_plan_details" },
  cancel_policy: { label: "Go to Cancel Company Policy", target: "cancel_policy" },
  company_information: { label: "Go to Company Information", target: "company_information" },
  renewal: { label: "Go to Renewal", target: "renewal" },
  support: { label: "Open Support Center", target: "support" },
};

export const employerDashboardActions: ConciergeActions<DestinationKey> = {
  domain: "employer_dashboard",
  spaRoutes: ["/employer-dashboard"],
  catalog,
  destinations,
};
